/* eslint-disable import/no-named-as-default */
/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Fab from '@material-ui/core/Fab';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import ClientPrivilegesTable from '../../components/ClientPrivilegesTable';
import ClientSubscriptionTable from '../../components/ClientSubscriptionTable';
import ClientAuditLogsTable from '../../components/ClientAuditLogsTable';
import ClientUsersTable from '../../components/ClientUsersTable';
import ClientBusinessLinesConfigure from '../../components/ClientBusinessLinesConfigure';
import ClientForm from '../AdminClientForm';
import { getClient, editClient, deleteClient, getClientAuditLogs } from '../../actions/clients';
import { selectUser } from '../../actions/users';
import ClientInfoGrid from '../../components/ClientInfoGrid';
import Loading from '../../components/Loading';

const useStyles = makeStyles(theme => ({
  hide: {
    display: 'none',
  },
  wrapper: {
    marginTop: theme.spacing(3),
  },
  loading: {
    marginTop: theme.spacing(7),
    position: 'fixed',
    left: '50%',
  },
  fab: {
    width: '60px',
    height: '60px',
    bottom: '30px',
    right: '10px',
  },
  sectionTitle: {
    borderBottom: '2px solid #DFDFDF',
    padding: theme.spacing(3),
  },
  grid: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
  },
}));

export const ClientDetail = props => {
  const {
    match,
    isEditing,
    getClient,
    selectedClient,
    isLoading,
    editClient,
    deleteClient,
    getClientAuditLogs,
    selectUser,
  } = props;
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    getClientAuditLogs(match.params.id);
    getClient(match.params.id);
  }, [getClient, getClientAuditLogs, match.params]);

  return (
    <>
      <Loading />
      {isEditing ? <ClientForm client={selectedClient} /> : null}
      <Container
        className={clsx(classes.wrapper, {
          [classes.hide]: isEditing,
        })}
        maxWidth="lg"
      >
        <CircularProgress
          className={clsx(classes.loading, {
            [classes.hide]: !isLoading,
          })}
        />
        <div className={clsx({ [classes.hide]: isLoading })}>
          <ClientInfoGrid client={selectedClient} />
        </div>

        <Grid
          container
          justify="flex-end"
          className={clsx({
            [classes.hide]: isLoading,
          })}
        >
          <div>
            <Fab
              color="primary"
              aria-label="actions"
              className={classes.fab}
              aria-controls="long-menu"
              aria-haspopup="true"
              onClick={event => setAnchorEl(event.currentTarget)}
            >
              <MoreVertIcon />
            </Fab>
            <Menu
              getContentAnchorEl={null}
              id="action-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              variant="selectedMenu"
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  editClient();
                }}
              >
                Edit Client
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  setDialogOpen(true);
                }}
              >
                Delete Client
              </MenuItem>
            </Menu>
          </div>
        </Grid>
        <ClientPrivilegesTable selectedClient={selectedClient} />
        <ClientSubscriptionTable selectedClient={selectedClient} />
        <ClientBusinessLinesConfigure selectedClient={selectedClient} />
        <ClientUsersTable selectedClient={selectedClient} onRowClick={selectUser} />
        <ClientAuditLogsTable selectedClient={selectedClient} />
      </Container>

      <ConfirmationDialog
        title="Delete Client?"
        description={`Are you sure you want to delete client ${selectedClient.name}? This action cannot be undone.`}
        primaryButtonText="Delete"
        onPrimaryButtonClick={() => {
          deleteClient(selectedClient.id);
        }}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setDialogOpen(false)}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

ClientDetail.defaultProps = {
  selectedClient: {},
  isEditing: false,
  match: {},
  getClient: () => {},
  selectUser: () => {},
  isLoading: false,
  editClient: () => {},
  deleteClient: () => {},
  getClientAuditLogs: () => {},
};

ClientDetail.propTypes = {
  selectedClient: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    billingAddressLine1: PropTypes.string,
    billingAddressLine2: PropTypes.string,
    billingAddressZip: PropTypes.string,
    billingAddressState: PropTypes.string,
    billingAddressCity: PropTypes.string,
    connection: PropTypes.string,
    defaultBusinessLineId: PropTypes.number,
    subscriptions: PropTypes.arrayOf(
      PropTypes.shape({
        applicationId: PropTypes.number,
        clientId: PropTypes.number,
        deletedAt: PropTypes.string,
        auth0ClientId: PropTypes.string,
        name: PropTypes.string,
      })
    ),
    businessLines: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        state: PropTypes.state,
        clientId: PropTypes.number,
      })
    ),
  }),
  isEditing: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  getClient: PropTypes.func,
  isLoading: PropTypes.bool,
  editClient: PropTypes.func,
  deleteClient: PropTypes.func,
  getClientAuditLogs: PropTypes.func,
  selectUser: PropTypes.func,
};

const mapStateToProps = state => {
  const { clients } = state;
  return {
    selectedClient: clients.selectedClient,
    isLoading: clients.isLoading,
    isEditing: clients.isEditing,
  };
};

const mapDispatchToProps = {
  getClient,
  editClient,
  deleteClient,
  getClientAuditLogs,
  selectUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(ClientDetail);
